import { Button, Flex, Modal, Text } from "@mantine/core";
import { DataTableColumn } from "mantine-datatable";
import { useMemo } from "react";
import { CSVLink } from "react-csv";

import { DatabaseMetadataPopover } from "../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import Table from "../../components/Table/Table.tsx";
import { TableRow } from "../../components/Table/types.ts";
import { Paymaster } from "../../types/paymaster.ts";
import { toHumanReadable } from "../../utils/string.ts";

interface Props {
  paymaster: Paymaster;
  opened?: boolean;
  onClose: () => void;
}

export const PaymasterCustomersModal = ({
  paymaster,
  opened = true,
  onClose,
}: Props) => {
  const hasCustomers = !!paymaster.customerPaymasters.length;

  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "id",
      hidden: true,
    },
    {
      accessor: "name",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Customer",
            fieldName: "name",
          }}
        >
          Customer Name
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "salesTeamName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Customer",
            fieldName: "salesTeamName",
          }}
        >
          Sales Team
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "salesName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Customer",
            fieldName: "salesName",
          }}
        >
          Sales Name
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "limitType",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitDetail",
            fieldName: "limitType",
          }}
        >
          Limit Type
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "limitName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitDetail",
            fieldName: "limitName",
          }}
        >
          Limit Name
        </DatabaseMetadataPopover>
      ),
    },
  ];

  const rows = useMemo(() => {
    return paymaster.customerPaymasters.map((item) => ({
      name: item.customer.name,
      salesTeamName: item.customer.salesTeamName,
      salesName: item.customer.salesName,
      limitType:
        item.limitDetail?.limitType &&
        toHumanReadable(item.limitDetail?.limitType),
      limitName: item.limitDetail?.limitName,
    }));
  }, [paymaster.customerPaymasters]);

  return (
    <Modal title="Customer Details" size="xl" opened={opened} onClose={onClose}>
      <Flex justify="space-between" align="center" pb={20} pl={5}>
        <Text weight="bolder">
          Total Customers: {paymaster.customerPaymasters.length}
        </Text>
        <CSVLink
          style={{ pointerEvents: !hasCustomers ? "none" : undefined }}
          filename={`paymaster_customers_${paymaster.name}.csv`}
          data={rows}
          headers={[
            { key: "name", label: "Customer Name" },
            { key: "salesTeamName", label: "Sales Team" },
            { key: "salesName", label: "Sales Name" },
            { key: "limitType", label: "Limit Type" },
            { key: "limitName", label: "Limit Name" },
          ]}
        >
          <Button variant="filledGolden" disabled={!hasCustomers}>
            Download data
          </Button>
        </CSVLink>
      </Flex>
      <Flex direction="column" gap={8}>
        <Table
          minWidth="350px"
          columns={columns}
          rows={rows}
          verticalAlignment="top"
        />
      </Flex>
    </Modal>
  );
};
